import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Save, Shield, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSnackbar } from 'notistack';
import { useRoles } from '../api/roles/use-roles';
import { ModulePermission } from '../models/role';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer, PageHeader } from '@/components/shared/layout';

const MODULES = [
    { key: 'USERS', label: 'Users', description: 'Manage system users and their accounts' },
    { key: 'LEADS', label: 'Leads', description: 'Manage customer leads and prospects' },
    { key: 'CUSTOMERS', label: 'Customers', description: 'Manage customer accounts and data' },
    { key: 'KYC', label: 'KYC', description: 'Handle Know Your Customer processes' },
    { key: 'ASSETS', label: 'Assets', description: 'Manage trading assets and instruments' },
    { key: 'INSTRUMENTS', label: 'Instruments', description: 'Configure trading instruments' },
    { key: 'TRADES', label: 'Trades', description: 'View and manage trade history' },
    { key: 'TRANSACTIONS', label: 'Transactions', description: 'Manage financial transactions' },
    {
        key: 'SYSTEM_SETTINGS',
        label: 'System Settings',
        description: 'Configure system parameters',
    },
    { key: 'AUDIT_LOG', label: 'Audit Log', description: 'View system audit logs' },
    { key: 'ROLES', label: 'Roles', description: 'Manage user roles and permissions' },
    { key: 'TRADING_ACCOUNTS', label: 'Trading Accounts', description: 'Manage trading accounts' },
    {
        key: 'PAYMENT_METHODS',
        label: 'Payment Methods',
        description: 'Manage payment methods and configurations',
    },
    {
        key: 'NOTES',
        label: 'Notes',
        description: 'Manage notes, tasks, and activities for leads and customers',
    },
    {
        key: 'NOTIFICATIONS',
        label: 'Notifications',
        description: 'List, view, and create notifications and manual notifications',
    },
    {
        key: 'INTEGRATIONS',
        label: 'Integrations',
        description: 'Manage third-party integrations (VOIP, Email, WhatsApp, SMS, Payment)',
    },
    {
        key: 'CAMPAIGNS',
        label: 'Campaigns',
        description: 'Manage lead-generation campaigns (Facebook Lead Ads, landing pages)',
    },
];

const SCOPES = [
    { key: 'mine', label: 'Mine', description: 'Access to own data only' },
    {
        key: 'my_reports',
        label: 'My Reports',
        description: 'Access to own reports and team reports',
    },
    { key: 'my_team', label: 'My Team', description: 'Access to team data' },
    { key: 'all', label: 'All', description: 'Access to all data' },
];

const ACTIONS = [
    { key: 'view', label: 'View', description: 'Read access to data' },
    { key: 'create', label: 'Create', description: 'Create new records' },
    { key: 'edit', label: 'Edit', description: 'Modify and update data' },
    { key: 'delete', label: 'Delete', description: 'Remove records' },
    { key: 'import', label: 'Import', description: 'Import data from external sources' },
    { key: 'export', label: 'Export', description: 'Export data to external formats' },
];

interface RoleFormData {
    name: string;
    type: 'system' | 'custom';
    description: string;
    is_system: boolean;
    permissions: Record<string, ModulePermission>;
}

const initialFormData: RoleFormData = {
    name: '',
    type: 'custom',
    description: '',
    is_system: false,
    permissions: MODULES.reduce(
        (acc, module) => ({
            ...acc,
            [module.key]: {
                module: module.key,
                view: { mine: false, my_reports: false, my_team: false, all: false },
                create: { mine: false, my_reports: false, my_team: false, all: false },
                edit: { mine: false, my_reports: false, my_team: false, all: false },
                delete: { mine: false, my_reports: false, my_team: false, all: false },
                import: { mine: false, my_reports: false, my_team: false, all: false },
                export: { mine: false, my_reports: false, my_team: false, all: false },
            },
        }),
        {}
    ),
};

const RoleEditPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { roles, updateRole, createRole } = useRoles();

    const [formData, setFormData] = useState<RoleFormData>(initialFormData);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const isEditing = Boolean(id);
    const role = isEditing ? roles?.find(r => r.id === id) : null;

    useEffect(() => {
        if (role) {
            setFormData({
                name: role.name,
                type: role.type,
                description: role.description || '',
                is_system: role.is_system,
                permissions: role.permissions,
            });
        }
    }, [role]);

    const handleInputChange = (field: keyof RoleFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    // --- Permission Matrix Logic ---
    const handlePermissionChange = (
        module: string,
        action: 'view' | 'create' | 'edit' | 'delete' | 'import' | 'export',
        scope: 'mine' | 'my_reports' | 'my_team' | 'all',
        value: boolean
    ) => {
        // Clone to avoid mutation
        const updated = JSON.parse(JSON.stringify(formData.permissions));

        // Define scope hierarchy: mine < my_reports < my_team < all
        const scopes = ['mine', 'my_reports', 'my_team', 'all'];
        const scopeIdx = scopes.indexOf(scope);

        if (value) {
            // When checking: enable all scopes from 'mine' up to current scope
            // This maintains the hierarchy: if you can access 'my_team', you can also access 'mine' and 'my_reports'
            for (let s = 0; s <= scopeIdx; s++) {
                updated[module][action][scopes[s]] = true;
            }
        } else {
            // When unchecking: disable all scopes from current scope up to 'all'
            // This maintains the hierarchy: if you can't access 'my_team', you can't access 'all' either
            for (let s = scopeIdx; s < scopes.length; s++) {
                updated[module][action][scopes[s]] = false;
            }
        }

        setFormData(prev => ({ ...prev, permissions: updated }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            enqueueSnackbar('Role name is required', { variant: 'error' });
            return;
        }

        setIsSaving(true);
        try {
            if (isEditing && role) {
                await updateRole.mutateAsync({ id: role.id, ...formData });
                enqueueSnackbar('Role updated successfully', { variant: 'success' });
            } else {
                await createRole.mutateAsync(formData);
                enqueueSnackbar('Role created successfully', { variant: 'success' });
            }
            setHasChanges(false);
            navigate('/roles');
        } catch (error: any) {
            const message = error.response?.data?.message || 'An error occurred';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const getPermissionSummary = (permissions: Record<string, any>) => {
        const modules = Object.keys(permissions);
        const totalPermissions = modules.length * 6 * 4; // modules * actions * scopes
        let grantedPermissions = 0;

        modules.forEach(module => {
            const modulePerms = permissions[module];
            ['view', 'create', 'edit', 'delete', 'import', 'export'].forEach(action => {
                ['mine', 'my_reports', 'my_team', 'all'].forEach(scope => {
                    if (modulePerms[action]?.[scope]) {
                        grantedPermissions++;
                    }
                });
            });
        });

        return {
            total: totalPermissions,
            granted: grantedPermissions,
            percentage: Math.round((grantedPermissions / totalPermissions) * 100),
        };
    };

    const permissionSummary = getPermissionSummary(formData.permissions);

    if (isEditing && !role) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-48" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                        <Skeleton className="h-64 w-full" />
                    </div>
                    <div className="lg:col-span-2">
                        <Skeleton className="h-96 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    const pageTitle = isEditing ? role?.name || 'Edit Role' : 'Create Role';

    return (
        <PageContainer>
            <PageHeader
                title={pageTitle}
                description={
                    isEditing
                        ? 'Modify role permissions and settings'
                        : 'Create a new role with custom permissions'
                }
                breadcrumbs={[{ label: 'Roles', to: '/roles' }, { label: pageTitle }]}
                backTo="/roles"
                backLabel="Back to roles"
                actions={
                    <>
                        <Button variant="outline" onClick={() => navigate('/roles')}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4" />
                                    Save Role
                                </>
                            )}
                        </Button>
                    </>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Basic Information */}
                <div className="lg:col-span-1">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Basic Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="text-sm font-medium mb-2 block">Role Name</label>
                                <Input
                                    placeholder="Enter role name"
                                    value={formData.name}
                                    onChange={e => handleInputChange('name', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">
                                    Description
                                </label>
                                <Textarea
                                    placeholder="Describe the role's purpose"
                                    value={formData.description}
                                    onChange={e => handleInputChange('description', e.target.value)}
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-2 block">Role Type</label>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant={formData.type === 'system' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleInputChange('type', 'system')}
                                    >
                                        System
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={formData.type === 'custom' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleInputChange('type', 'custom')}
                                    >
                                        Custom
                                    </Button>
                                </div>
                            </div>

                            <div className="pt-4 border-t">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">Permissions Summary</span>
                                    <Badge variant="outline">{permissionSummary.percentage}%</Badge>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                        className="bg-primary h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${permissionSummary.percentage}%` }}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {permissionSummary.granted} of {permissionSummary.total}{' '}
                                    permissions granted
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Permissions */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Permissions
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="detailed" className="w-full">
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="detailed">Detailed View</TabsTrigger>
                                    <TabsTrigger value="summary">Summary View</TabsTrigger>
                                </TabsList>

                                <TabsContent value="detailed" className="mt-6">
                                    <div className="space-y-6">
                                        {MODULES.map(module => (
                                            <div key={module.key} className="border rounded-lg p-4">
                                                <div className="mb-4">
                                                    <h3 className="font-semibold">
                                                        {module.label}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        {module.description}
                                                    </p>
                                                </div>

                                                <div className="grid grid-cols-5 gap-4">
                                                    <div className="text-sm font-medium text-muted-foreground">
                                                        Action / Scope
                                                    </div>
                                                    {SCOPES.map(scope => (
                                                        <div
                                                            key={scope.key}
                                                            className="text-sm font-medium text-center"
                                                        >
                                                            {scope.label}
                                                        </div>
                                                    ))}

                                                    {ACTIONS.map(action => (
                                                        <React.Fragment key={action.key}>
                                                            <div className="text-sm font-medium flex items-center gap-2">
                                                                {action.label}
                                                                <span className="text-xs text-muted-foreground">
                                                                    {action.description}
                                                                </span>
                                                            </div>
                                                            {SCOPES.map(scope => (
                                                                <div
                                                                    key={scope.key}
                                                                    className="flex justify-center"
                                                                >
                                                                    <Checkbox
                                                                        checked={
                                                                            formData.permissions[
                                                                                module.key
                                                                            ]?.[
                                                                                action.key as
                                                                                    | 'view'
                                                                                    | 'create'
                                                                                    | 'edit'
                                                                                    | 'delete'
                                                                                    | 'import'
                                                                                    | 'export'
                                                                            ]?.[
                                                                                scope.key as
                                                                                    | 'mine'
                                                                                    | 'my_reports'
                                                                                    | 'my_team'
                                                                                    | 'all'
                                                                            ] || false
                                                                        }
                                                                        onCheckedChange={checked =>
                                                                            handlePermissionChange(
                                                                                module.key,
                                                                                action.key as
                                                                                    | 'view'
                                                                                    | 'create'
                                                                                    | 'edit'
                                                                                    | 'delete'
                                                                                    | 'import'
                                                                                    | 'export',
                                                                                scope.key as
                                                                                    | 'mine'
                                                                                    | 'my_reports'
                                                                                    | 'my_team'
                                                                                    | 'all',
                                                                                Boolean(checked)
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </TabsContent>

                                <TabsContent value="summary" className="mt-6">
                                    <div className="space-y-4">
                                        {MODULES.map(module => {
                                            const modulePerms = formData.permissions[module.key];
                                            const hasAnyPermission = ACTIONS.some(action =>
                                                SCOPES.some(
                                                    scope =>
                                                        modulePerms?.[
                                                            action.key as
                                                                | 'view'
                                                                | 'create'
                                                                | 'edit'
                                                                | 'delete'
                                                                | 'import'
                                                                | 'export'
                                                        ]?.[
                                                            scope.key as
                                                                | 'mine'
                                                                | 'my_reports'
                                                                | 'my_team'
                                                                | 'all'
                                                        ]
                                                )
                                            );

                                            return (
                                                <div
                                                    key={module.key}
                                                    className="flex items-center justify-between p-3 border rounded-lg"
                                                >
                                                    <div>
                                                        <h3 className="font-medium">
                                                            {module.label}
                                                        </h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            {module.description}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {hasAnyPermission ? (
                                                            <Badge className="bg-success/10 text-success">
                                                                <Check className="h-3 w-3 me-1" />
                                                                Permissions Granted
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-muted-foreground"
                                                            >
                                                                <X className="h-3 w-3 me-1" />
                                                                No Permissions
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </PageContainer>
    );
};

export default RoleEditPage;
